import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || `/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 2FA required response - don't redirect to login
    if (error.response?.status === 403 && error.response?.data?.error === '2fa_required') {
      return Promise.reject(error);
    }

    // Handle IP whitelist block - don't redirect to login
    if (error.response?.status === 403 && error.response?.data?.error === 'ip_not_allowed') {
      return Promise.reject(error);
    }

    // Handle rate limiting (429)
    if (error.response?.status === 429) {
      const retryAfter = error.response.data?.retry_after || 60;
      error.retryAfter = retryAfter;
      error.isRateLimited = true;
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      // Don't clear token if it's a 2FA temp token being used for validation
      const is2FAValidation = error.config?.url?.includes('/auth/2fa/validate');
      if (!is2FAValidation) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }),
  logout: () => api.post('/auth/logout'),
  // 2FA
  setup2FA: () => api.post('/auth/2fa/setup'),
  verify2FA: (code) => api.post('/auth/2fa/verify', { code }),
  validate2FA: (code, recoveryCode) => {
    const body = {};
    if (recoveryCode) body.recoveryCode = recoveryCode;
    else body.code = code;
    return api.post('/auth/2fa/validate', body);
  },
  disable2FA: (userId, totpCode) => api.post('/auth/2fa/disable', { userId, totpCode }),
  get2FAStatus: () => api.get('/auth/2fa/status'),
  // Impersonation
  impersonate: (targetUserId, reason, totpCode) => api.post('/auth/impersonate', { target_user_id: targetUserId, reason, totp_code: totpCode }),
  stopImpersonation: () => api.post('/auth/impersonate/stop'),
  getActiveImpersonation: () => api.get('/auth/impersonate/active'),
  getImpersonationHistory: (params) => api.get('/auth/impersonate/history', { params }),
};

export const securityAPI = {
  getAuditLog: (params) => api.get('/security/audit-log', { params }),
  getAuditLogActions: () => api.get('/security/audit-log/actions'),
  getAuditLogUsers: () => api.get('/security/audit-log/users'),
  exportAuditLog: (params) => api.get('/security/audit-log/export', { params, responseType: 'blob' }),
  getAlerts: (params) => api.get('/security/audit-log/alerts', { params }),
  resolveAlert: (id) => api.post(`/security/audit-log/alerts/${id}/resolve`),
  // IP Whitelist
  getIpWhitelist: (userId) => api.get('/security/ip-whitelist', { params: userId ? { user_id: userId } : {} }),
  addIpWhitelist: (data) => api.post('/security/ip-whitelist', data),
  deleteIpWhitelist: (id) => api.delete(`/security/ip-whitelist/${id}`),
  getMyIp: () => api.get('/security/ip-whitelist/my-ip'),
  // Security Settings
  getSecuritySettings: () => api.get('/security/settings'),
  updateSecuritySetting: (key, value) => api.put(`/security/settings/${key}`, { value }),
  // Data Masking / Reveal
  revealSensitiveData: ({ fieldType, resourceType, resourceId, totpCode }) =>
    api.post('/security/reveal', {
      field_type: fieldType,
      resource_type: resourceType,
      resource_id: resourceId,
      totp_code: totpCode,
    }),
  getRevealHistory: (params) => api.get('/security/reveal-history', { params }),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getRoles: () => api.get('/users/roles'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { newPassword }),
  getLogs: (params) => api.get('/users/logs', { params }),
  getLogFilters: () => api.get('/users/logs/filters'),
  getPermissionsList: () => api.get('/users/permissions-list'),
  // RBAC scope options
  getScopeTeams: () => api.get('/users/scope-options/teams'),
  getScopeBuyers: () => api.get('/users/scope-options/buyers'),
  getUserScopeDetails: (id) => api.get(`/users/${id}/scope-details`),
  // Buyers CRUD
  getBuyers: () => api.get('/users/buyers'),
  createBuyer: (data) => api.post('/users/buyers', data),
  updateBuyer: (id, data) => api.put(`/users/buyers/${id}`, data),
  deleteBuyer: (id) => api.delete(`/users/buyers/${id}`),
};

export const claudeAPI = {
  chat: (messages, max_tokens = 2000) => api.post('/claude/chat', { messages, max_tokens }),
  declineAnalysis: (data) => api.post('/claude/decline-analysis', data)
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data)
};

export const billingAPI = {
  getPartners: () => api.get('/billing'),
  updatePartner: (id, data) => api.put(`/billing/${id}`, data),
  uploadData: (data) => api.post('/billing/upload', data),
  getDebtBreakdown: (partnerId) => api.get(`/billing/${partnerId}/debt-breakdown`),
  getPayments: (partnerId) => api.get(`/billing/${partnerId}/payments`),
  addPayment: (partnerId, data) => api.post(`/billing/${partnerId}/payments`, data),
  updatePayment: (paymentId, data) => api.put(`/billing/payments/${paymentId}`, data),
  deletePayment: (paymentId) => api.delete(`/billing/payments/${paymentId}`),
  getUploads: () => api.get('/billing/uploads/history'),
  getRates: () => api.get('/billing/rates'),
  // Adjustments
  getAdjustments: (partnerId) => api.get(`/billing/${partnerId}/adjustments`),
  upsertAdjustment: (partnerId, data) => api.post(`/billing/${partnerId}/adjustments`, data),
  deleteAdjustment: (adjustmentId) => api.delete(`/billing/adjustments/${adjustmentId}`)
};

export const keitaroAPI = {
  getSettings: () => api.get('/keitaro/settings'),
  saveSettings: (data) => api.put('/keitaro/settings', data),
  triggerSync: (fromDate, fullResync) => api.post('/keitaro/sync', { fromDate, fullResync }),
  getSyncStatus: () => api.get('/keitaro/sync/status'),
  getSyncHistory: () => api.get('/keitaro/sync/history'),
  getStats: (params) => api.get('/keitaro/stats', { params }),
  getSummary: (params) => api.get('/keitaro/summary', { params }),
  getFilters: (params) => api.get('/keitaro/filters', { params }),
  getBreakdown: (params) => api.get('/keitaro/stats/breakdown', { params }),
  getDashboard: (period) => api.get('/keitaro/dashboard', { params: { period } }),
  getDashboardHourly: (period) => api.get('/keitaro/dashboard/hourly', { params: { period } }),
  getDashboardTop: (groupBy, period, limit) => api.get('/keitaro/dashboard/top', { params: { groupBy, period, ...(limit ? { limit } : {}) } }),
};

export const teamsAPI = {
  getAll: () => api.get('/teams'),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  getBuyers: (id) => api.get(`/teams/${id}/buyers`),
  addBuyer: (id, buyerName) => api.post(`/teams/${id}/buyers`, { buyerName }),
  removeBuyer: (id, buyerName) => api.delete(`/teams/${id}/buyers/${encodeURIComponent(buyerName)}`),
  getAvailableBuyers: () => api.get('/teams/available-buyers'),
};

export const gdriveAPI = {
  listFiles: () => api.get('/gdrive/files'),
  downloadFile: (fileId) => api.get(`/gdrive/download/${fileId}`),
};

export const qualityAPI = {
  save: (data) => api.post('/quality/save', data),
  list: () => api.get('/quality/list'),
  getDates: (partnerName) => api.get(`/quality/dates/${encodeURIComponent(partnerName)}`),
  getReport: (partnerName, date) => api.get(`/quality/report/${encodeURIComponent(partnerName)}/${date}`),
  deleteReport: (id) => api.delete(`/quality/report/${id}`),
  getBuyerTeams: () => api.get('/quality/buyer-teams'),
  getTeams: () => api.get('/quality/teams'),
  saveBuyerTeam: (buyerName, teamName) => api.post('/quality/buyer-teams', { buyerName, teamName }),
  bulkSaveBuyerTeams: (mappings) => api.post('/quality/buyer-teams/bulk', { mappings }),
  deleteBuyerTeam: (buyerName) => api.delete(`/quality/buyer-teams/${encodeURIComponent(buyerName)}`),
  fillMissing: (buyers, beforeDate) => api.post('/quality/fill-missing', { buyers, beforeDate }),
};

export const keitaroManagerAPI = {
  getOffers: (params) => api.get('/keitaro-manager/offers', { params }),
  getAffiliateNetworks: () => api.get('/keitaro-manager/affiliate-networks'),
  getCountries: () => api.get('/keitaro-manager/countries'),
  editOffers: (data) => api.post('/keitaro-manager/offers/edit', data),
  cloneOffers: (data) => api.post('/keitaro-manager/offers/clone', data),
  // Stream Assign
  getCampaignGroups: () => api.get('/keitaro-manager/campaign-groups'),
  getCampaigns: (groupId) => api.get('/keitaro-manager/campaigns', { params: { group_id: groupId } }),
  getStreams: (campaignId) => api.get('/keitaro-manager/streams', { params: { campaign_id: campaignId } }),
  getOffersForStreams: (buyerName) => api.get('/keitaro-manager/offers-for-streams', { params: { buyer_name: buyerName } }),
  addOffersToStreams: (assignments) => api.post('/keitaro-manager/streams/add-offers', { assignments }),
};

export const trafficShieldAPI = {
  // Settings (admin)
  getSettings: () => api.get('/traffic-shield/settings'),
  updateSettings: (settings) => api.put('/traffic-shield/settings', { settings }),
  testConnection: (service) => api.post(`/traffic-shield/settings/test/${service}`),

  // Links
  getLinks: () => api.get('/traffic-shield/links'),
  getLink: (id) => api.get(`/traffic-shield/links/${id}`),
  createLink: (data) => api.post('/traffic-shield/links', data),
  updateLink: (id, data) => api.put(`/traffic-shield/links/${id}`, data),
  updatePwaUrl: (id, pwaUrl, pwaUrls) => api.put(`/traffic-shield/links/${id}/pwa`, { pwa_url: pwaUrl, pwa_urls: pwaUrls }),
  deleteLink: (id) => api.delete(`/traffic-shield/links/${id}`),
  softDeleteLink: (id) => api.delete(`/traffic-shield/links/${id}/soft`),
  restoreLink: (id) => api.put(`/traffic-shield/links/${id}/restore`),
  regenerateWhitePage: (id, data) => api.put(`/traffic-shield/links/${id}/white-page`, data),
  getLinkHistory: (id) => api.get(`/traffic-shield/links/${id}/history`),

  // Domains
  getDomains: (params) => api.get('/traffic-shield/domains', { params }),
  getAvailableDomains: () => api.get('/traffic-shield/domains/available'),
  getDomain: (id) => api.get(`/traffic-shield/domains/${id}`),
  createDomain: (data) => api.post('/traffic-shield/domains', data),
  updateDomain: (id, data) => api.put(`/traffic-shield/domains/${id}`, data),
  deleteDomain: (id) => api.delete(`/traffic-shield/domains/${id}`),

  // Stats
  getStats: () => api.get('/traffic-shield/stats'),

  // Namecheap
  searchDomains: (keyword, tlds) => api.post('/traffic-shield/namecheap/search', { keyword, tlds }),
  checkDomains: (domains) => api.post('/traffic-shield/namecheap/check', { domains }),
  purchaseDomain: (domain, years) => api.post('/traffic-shield/namecheap/purchase', { domain, years }),
  getNamecheapBalance: () => api.get('/traffic-shield/namecheap/balance'),

  // Cloudflare
  addCloudflareZone: (domain) => api.post('/traffic-shield/cloudflare/add-zone', { domain }),
  setupCloudflareDNS: (zoneId, vpsIp) => api.post('/traffic-shield/cloudflare/setup-dns', { zoneId, vpsIp }),
  fullCloudflareSetup: (domain) => api.post('/traffic-shield/cloudflare/full-setup', { domain }),
  getCloudflareZoneStatus: (zoneId) => api.get(`/traffic-shield/cloudflare/zone-status/${zoneId}`),

  // White Page Generator
  parseGooglePlay: (url) => api.post('/traffic-shield/white-page/parse', { google_play_url: url }),
  generateWhitePage: (data) => api.post('/traffic-shield/white-page/generate', data),

  // VPS
  getVpsInfo: () => api.get('/traffic-shield/vps/info'),

  // Builder (SSE stream)
  builderCreateSSE: (data) => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/traffic-shield/builder/create`;
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
  },

  // Admin: Cloudflare Accounts
  getCloudflareAccounts: () => api.get('/traffic-shield/admin/cloudflare-accounts'),
  createCloudflareAccount: (data) => api.post('/traffic-shield/admin/cloudflare-accounts', data),
  updateCloudflareAccount: (id, data) => api.put(`/traffic-shield/admin/cloudflare-accounts/${id}`, data),
  deleteCloudflareAccount: (id) => api.delete(`/traffic-shield/admin/cloudflare-accounts/${id}`),
  testCloudflareAccount: (id) => api.post(`/traffic-shield/admin/cloudflare-accounts/${id}/test`),

  // Admin: Registrant
  getRegistrant: () => api.get('/traffic-shield/admin/registrant'),
  updateRegistrant: (data) => api.put('/traffic-shield/admin/registrant', data),

  // Admin: General Settings
  getGeneralSettings: () => api.get('/traffic-shield/admin/general-settings'),
  updateGeneralSettings: (data) => api.put('/traffic-shield/admin/general-settings', data),
};

export const kpiAPI = {
  getTargets: (params) => api.get('/kpi/targets', { params }),
  getTarget: (id) => api.get(`/kpi/targets/${id}`),
  createTarget: (data) => api.post('/kpi/targets', data),
  updateTarget: (id, data) => api.put(`/kpi/targets/${id}`, data),
  deleteTarget: (id) => api.delete(`/kpi/targets/${id}`),
  getTeamKpi: (params) => api.get('/kpi/team-kpi', { params }),
  getBuyerKpi: (params) => api.get('/kpi/buyer-kpi', { params }),
  getStats: () => api.get('/kpi/stats'),
  recalculate: () => api.post('/kpi/recalculate'),
  getTeamsList: () => api.get('/kpi/teams-list'),
  getBuyersList: () => api.get('/kpi/buyers-list'),
};

export default api;
